import { InjectionToken } from '@angular/core';

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import buildInfo from '@valk-nx/build-info.json';

import type { VimeoInitialQuality } from './interfaces/video.interface';

export const languages = ['nl', 'ca', 'en', 'es', 'de', 'fr'] as const;
export const LANGUAGE = new InjectionToken<string>('valk-language');
export const ASSETS_ROOT = new InjectionToken<string>('valk-core');
export const HOTEL_SLUG = new InjectionToken<string>('hotel-slug');
export const themes = ['app', 'deals', 'global', 'hotels', 'resort'] as const;
export type Theme = `${(typeof themes)[number]}-theme`;
export const THEME = new InjectionToken<Theme>('theme');
export const MAPS_SERVICE_KEY = new InjectionToken<string>('maps-service-key');
export const RECAPTCHA_V2_SITE_KEY = new InjectionToken<string>(
	'recaptcha-v2-site-key',
);
export const USE_NATIVE_NAVIGATION = new InjectionToken<string>(
	'native-navigation',
);
export const BFF_URL = new InjectionToken<string>('bff-url');
// Rendition Vimeo players start on, so an app can set it from its environment
// without threading an input through every carousel in between. Left unprovided
// the player keeps Vimeo's own `auto`.
export const VIDEO_INITIAL_QUALITY = new InjectionToken<VimeoInitialQuality>(
	'video-initial-quality',
);

export const WITH_CREDENTIALS = new InjectionToken<{
	withCredentials: boolean;
}>('with-credentials');

export const IS_HOTELSITE_BOOKINGTOOL = new InjectionToken<boolean>(
	'is-hotelsite-bookingtool',
);
export const BOOKINGTOOL_HOST = new InjectionToken<string>('bookingtool-host');
// Set on the hotelsite booking tool only, where the language lives in the
// serving path outside the router; null in every other mode.
export const BOOKINGTOOL_LANGUAGE = new InjectionToken<string | null>(
	'bookingtool-language',
);

/* v8 ignore start */
export function createTranslateLoader(assetsRoot: string): {
	prefix: string;
	suffix: string;
} {
	return {
		prefix: `${assetsRoot}/i18n/`,
		suffix: `.json?v=${buildInfo?.cacheKey || 1}`,
	};
}
/* v8 ignore stop */

export const excludedLanguageFromUrl = 'nl';
export const fallbackLanguage = languages[0];
export type Language = (typeof languages)[number];
export type LanguageRecord = Record<Language, string>;

export const languagesLabels: LanguageRecord = {
	ca: 'Catalunya',
	de: 'Deutsch',
	en: 'English',
	es: 'Español',
	fr: 'Français',
	nl: 'Nederlands',
};
export const i18nLanguageLabels: LanguageRecord = {
	ca: 'global.spanish',
	de: 'global.german',
	en: 'global.english',
	es: 'global.spanish',
	fr: 'global.french',
	nl: 'global.dutch',
};
export const minimumSpendableLoyalAmount = 250;

export type LinkTarget = '_blank' | '_self' | '';

export type QueryParamValue = string | number | boolean | null | undefined;

export function toQueryString(
	params: Record<string, QueryParamValue> | null | undefined,
): string {
	if (!params) {
		return '';
	}

	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== null && value !== undefined) {
			search.set(key, String(value));
		}
	}

	return search.toString();
}

export interface SuccessResponse {
	succes: boolean;
}

export const PRICING_TOOLTIP_OCCUPANCY = {
	persons: 2,
	rooms: 1,
};

export const INITIAL = 'initial';
export const LOADING = 'loading';
export const SUCCESS = 'success';
export const FAILED = 'failed';
export const CANCELLED = 'cancelled';
export const UNAUTHORIZED = 'unauthorized';
export const FORBIDDEN = 'forbidden';
export const RECAPTCHA_FORBIDDEN = 'recaptcha-forbidden';

export type RequestStatus =
	typeof INITIAL | typeof LOADING | typeof SUCCESS | typeof FAILED;

/* v8 ignore start */
export function cn(...inputs: ClassValue[]) {
	// twMerge merges tailwind classes that are in the same "group".
	// This results in always having the same output regardless of the order of the classes being generated by tailwind when developing
	// clsx is a utility for conditionally joining class names together
	// combining these two utilities allows us to have a consistent class name output, used a lot by developers also using tailwind
	return twMerge(clsx(inputs));
}
/* v8 ignore stop */
